import React, { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Dialog } from '../../components/ui/dialog'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { axiosGet, axiosPatch, axiosPost } from '../../util/api'
import { Tag } from '@blueprintjs/core'
import { Menu, MenuDivider, MenuItem } from '../../components/ui/menu'
import { clearAndFetchUser } from '../../actions/user'
import { money } from '../../util/formatters'
import { format, isBefore } from 'date-fns'
import { FormInput } from '../../components/auth/common'
import { SubscriptionStatus } from '../../types/billing'
import { Layout } from '../../components/Layout'
import { Toasts } from '../../components/toaster/Toast'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { Popover } from '../../components/ui/popover'
import { Tooltip } from '../../components/ui/tooltip'
import User = Models.User
import { EllipsisMenu } from '../../icons/icons'
import { Button } from '../../components/ui/button'
import {
    type AgGridColumnDef,
    AgGridTable,
    DummyHeader,
    type DummyHeaderProps,
} from '../../components/table2/AgGridTable'
import { TableStore } from '../../components/table2/table_store'
import { userStore } from '../../stores/user_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import type { CellProps } from '../../components/table2/ad_grid_columns'
import { HeaderContextMenu } from '../../components/table2/react_data_grid_columns'
import { observer } from 'mobx-react-lite'
import type { FilterI } from '../../constants/filter'

type AdminUser = User & {
    subscription?: string
    __actions?: string
}

interface ActionsProps {
    user: User
    onGiveAdSpendCreditClick: (user: User) => any
    onChangeEmail: (user: User) => any
    onAddTrialDays: (user: User) => any
    onReactiveSubscription: (user: User) => any
}

const Actions = ({
    user,
    onGiveAdSpendCreditClick,
    onAddTrialDays,
    onChangeEmail,
    onReactiveSubscription,
}: ActionsProps) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const impersonate = () => {
        axiosGet(`admin/impersonate?impersonate=${user.id}`)
            .then(() => {
                dispatch(clearAndFetchUser())
                navigate('/dashboard')
            })
            .catch((err) => {
                console.log('err', err)
            })
    }

    const canAddTrialDays =
        (user.subscriptions?.length ?? 0) > 0 &&
        user.subscriptions?.[0].status === SubscriptionStatus.Trialing

    const hasActiveSubscription = user?.active_subscription

    const menu = useMemo(() => {
        return (
            <Menu>
                <MenuItem text="Impersonate" onClick={impersonate} />
                <MenuDivider />
                <MenuItem text="Give coins" onClick={() => onGiveAdSpendCreditClick(user)} />
                <MenuItem text="Change email" onClick={() => onChangeEmail(user)} />
                <MenuItem
                    text="Add trial days"
                    disabled={!canAddTrialDays}
                    onClick={() => onAddTrialDays(user)}
                />
                <MenuDivider />
                <MenuItem
                    text="Reactive subscription"
                    disabled={!!hasActiveSubscription}
                    onClick={() => onReactiveSubscription(user)}
                />
            </Menu>
        )
    }, [user])

    return <EllipsisMenu content={menu} />
}

const StaticHeader = observer(
    <T,>({ column: { colDef }, context: { store } }: DummyHeaderProps<T>) => (
        <HeaderContextMenu columnName={colDef.field as string} store={store}>
            <div className="w-full flex-col">
                <div className="flex items-center">{String(colDef.headerName ?? colDef.field)}</div>
            </div>
        </HeaderContextMenu>
    ),
)

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

const IdCellRenderer = ({ data: { id } }: CellProps<AdminUser>) => (
    <Link to={`/admin/users/${id}`}>{id}</Link>
)

const SubscriptionCellRenderer = ({ data: user }: CellProps<AdminUser>) => {
    // if user has active subscription get the status from there
    // if not, get the status from the 0th subscription in the array
    let subscriptionStatus
    if (user?.active_subscription) {
        subscriptionStatus = user?.active_subscription.status
    } else {
        subscriptionStatus = user?.subscriptions?.[0]?.status
    }

    const generateWrapperContent = () => {
        if (user?.subscriptions?.length == 0) {
            return
        }

        return (
            <div className="p-2">
                <ul>
                    {user.subscriptions?.map((sub, i) => (
                        <li key={i}>{sub.subscription_id ?? 'dummy'}</li>
                    ))}
                </ul>
            </div>
        )
    }

    const wrapper = (element: ReactNode) => {
        return (
            <div>
                <Popover minimal interactionKind="hover" content={generateWrapperContent()}>
                    {element}
                </Popover>
            </div>
        )
    }

    switch (subscriptionStatus) {
        case SubscriptionStatus.Incomplete:
            return wrapper('Incomplete')
        case SubscriptionStatus.IncompleteExpired:
            return wrapper('Incomplete Expired')
        case SubscriptionStatus.Trialing:
            return wrapper(<Tag minimal>Trialing</Tag>)
        case SubscriptionStatus.Active:
            return wrapper(
                <Tag minimal intent="success">
                    Active
                </Tag>,
            )
        case SubscriptionStatus.PastDue:
            return wrapper(
                <Tag minimal intent="warning">
                    Past Due
                </Tag>,
            )
        case SubscriptionStatus.Canceled:
            return wrapper(<Tag minimal>Canceled</Tag>)
        case SubscriptionStatus.Unpaid:
            return wrapper(
                <Tag minimal intent="danger">
                    Unpaid
                </Tag>,
            )
        default:
            return 'Unknown'
    }
}

const AdSpendCreditsCellRenderer = ({ data: user }: CellProps<AdminUser>) => {
    return (
        <Tooltip
            content={
                user.ad_spend_credit_log && user.ad_spend_credit_log.length > 0 ? (
                    <div>
                        {user.ad_spend_credit_log?.map((log) => (
                            <ul key={log.id}>
                                <li>
                                    {format(new Date(log.created_at), 'M/d/yy H:mm a')} -{' '}
                                    {money(log.amount)} - {log.session_id}
                                </li>
                            </ul>
                        ))}
                    </div>
                ) : undefined
            }
        >
            <div>{money(user.ad_spend_credits)}</div>
        </Tooltip>
    )
}

export const AdminUsers = observer(() => {
    const [user, setUser] = useState<Models.User | null>(null)
    const [active, setActive] = useState<string | null>(null)
    const [store] = useState<TableStore<AdminUser>>(new TableStore(userStore, 'Users'))
    const savedFiltersStore = useSavedFiltersStore()

    const load = (params: any) => {
        return axiosGet('admin/users', params)
    }

    const tableHooks = useTableHooksFromStore(load, {}, false, store, undefined, {
        requireProfile: false,
    })

    const columns = useMemo<AgGridColumnDef<AdminUser>[]>(
        () => [
            {
                field: 'id',
                headerName: 'Id',
                headerComponent: agGridComponent(DummyHeader),
                cellRenderer: agGridComponent(IdCellRenderer),
            },
            {
                field: 'name',
                headerName: 'Name',
                headerComponent: agGridComponent(DummyHeader),
                width: 300,
            },
            {
                field: 'email',
                headerName: 'Email',
                headerComponent: agGridComponent(DummyHeader),
                width: 300,
            },
            {
                field: 'subscription',
                headerName: 'Subscription',
                headerComponent: agGridComponent(StaticHeader),
                cellRenderer: agGridComponent(SubscriptionCellRenderer),
            },
            {
                field: 'ad_spend_credits',
                headerName: 'Ad Spend Credits',
                headerComponent: agGridComponent(StaticHeader),
                cellRenderer: agGridComponent(AdSpendCreditsCellRenderer),
            },
            {
                field: '__actions',
                headerName: 'Actions',
                headerComponent: agGridComponent(StaticHeader),
                cellRenderer: agGridComponent(({ data: user }: CellProps<AdminUser>) => (
                    <Actions
                        user={user}
                        onGiveAdSpendCreditClick={(user: User) => {
                            setActive('give-ad-spend-credit')
                            setUser(user)
                        }}
                        onAddTrialDays={(user: User) => {
                            setActive('add-trial-days')
                            setUser(user)
                        }}
                        onChangeEmail={(user: User) => {
                            setActive('change-email')
                            setUser(user)
                        }}
                        onReactiveSubscription={(user: User) => {
                            setActive('reactivate-subscription')
                            setUser(user)
                        }}
                    />
                )),
            },
        ],
        [],
    )

    return (
        <Layout primary="Users">
            <AgGridTable
                name={'Users'}
                showHeader={false}
                tableHooks={tableHooks}
                store={store}
                savedFiltersStore={savedFiltersStore}
                filters={filters}
                dateSelect
                search
                searchPlaceholder="Search by email, name, or id"
                columns={columns}
                rowHeight={48}
            />
            {active === 'give-ad-spend-credit' && (
                <GiveAdSpendCredit
                    user={user}
                    close={() => {
                        setUser(null)
                        setActive(null)
                    }}
                    onSave={() => {
                        tableHooks.reload()
                    }}
                />
            )}
            {active === 'add-trial-days' && (
                <AddTrialDays
                    user={user}
                    close={() => {
                        setUser(null)
                        setActive(null)
                    }}
                    onSave={() => {
                        tableHooks.reload()
                    }}
                />
            )}
            {active === 'change-email' && (
                <ChangeEmail
                    user={user}
                    close={() => {
                        setUser(null)
                        setActive(null)
                    }}
                    onSave={() => {
                        tableHooks.reload()
                    }}
                />
            )}
            {active === 'reactivate-subscription' && (
                <ReactiveSubscription
                    user={user}
                    close={() => {
                        setUser(null)
                        setActive(null)
                    }}
                    onSave={() => {
                        tableHooks.reload()
                    }}
                />
            )}
        </Layout>
    )
})

interface UserActionProps {
    user: Models.User | null
    close: () => void
    onSave: () => void
}

const GiveAdSpendCredit = ({ user, close, onSave }: UserActionProps) => {
    const [sessionId, setSessionId] = useState('')
    const [amount, setAmount] = useState(0)
    const [saving, setSaving] = useState(false)

    const save = () => {
        setSaving(true)
        axiosPost('admin/users/give-ad-spend-credit', {
            amount,
            session_id: sessionId,
            user_id: user?.id,
        })
            .then(() => {
                Toasts.success('Coins added')
                setSaving(false)
                close()
                onSave()
            })
            .catch(() => {
                setSaving(false)
            })
    }

    return (
        <Dialog
            isOpen={!!user}
            onClose={close}
            className="p-3"
            canEscapeKeyClose
            canOutsideClickClose
        >
            <p>
                You are adding coins to {user?.email}'s account (user id {user?.id}).
            </p>
            <p>Enter a negative number of coins to remove coins from an account.</p>
            <FormInput
                disabled={saving}
                type="text"
                label="Session Id or Description"
                value={sessionId}
                onChange={setSessionId}
            />
            <FormInput
                disabled={saving}
                type="money"
                label="Number of coins ($1 = 1 coin)"
                value={amount.toString()}
                onChange={(v) => setAmount(parseInt(v))}
            />
            <Button loading={saving} intent="primary" text="Give Coins" onClick={save} />
        </Dialog>
    )
}

const AddTrialDays = ({ user, close, onSave }: UserActionProps) => {
    const [days, setDays] = useState(0)
    const [saving, setSaving] = useState(false)

    const save = () => {
        setSaving(true)
        axiosPost('admin/users/add-trial-days', {
            days,
            user_id: user?.id,
        })
            .then(() => {
                Toasts.success(`${days} trial days added`)
                setSaving(false)
                close()
                onSave()
            })
            .catch(() => {
                setSaving(false)
            })
    }

    let end = null
    let endDate = null
    let before = false
    if (user?.subscriptions?.[0]?.trial_end) {
        endDate = new Date(1000 * user?.subscriptions?.[0]?.trial_end)
        end = format(endDate, 'M/d/yy H:mm a')
        before = isBefore(endDate, new Date())
    }

    return (
        <Dialog
            isOpen={!!user}
            onClose={close}
            className="p-3"
            canEscapeKeyClose
            canOutsideClickClose
        >
            <p>
                You are adding trial days to {user?.email}'s account (user id {user?.id}). This will
                add {days} days to the end of their trial period.
            </p>
            <p>
                If their trial ended 4 days ago and you add 7 days, then they will have 3 more days
                of usage.
            </p>
            <p>
                {user?.name}'s trial {before ? 'ended' : 'ends on'} {end}
            </p>
            <FormInput
                disabled={saving}
                type="number"
                label="Days"
                value={days.toString()}
                onChange={(v) => setDays(parseInt(v))}
            />
            <Button
                loading={saving}
                intent="primary"
                text="Give Additional Trial Days"
                onClick={save}
            />
        </Dialog>
    )
}

const ChangeEmail = ({ user, close, onSave }: UserActionProps) => {
    const [email, setEmail] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setEmail(user?.email ?? '')
    }, [])

    const save = () => {
        setSaving(true)
        axiosPatch(`admin/user/${user?.id}`, {
            email,
        })
            .then(() => {
                Toasts.success(`Email changed to ${email}`)
                setSaving(false)
                close()
                onSave()
            })
            .catch(() => {
                setSaving(false)
            })
    }

    return (
        <Dialog
            isOpen={!!user}
            onClose={close}
            className="p-3"
            canEscapeKeyClose
            canOutsideClickClose
        >
            <p>
                You are changing the email for {user?.email}'s account (user id {user?.id}).
            </p>
            <p>
                You should have confirmation that the user owns both email addresses, by having them
                send an email change request from both addresses.
            </p>
            <FormInput
                disabled={saving}
                type="email"
                label="Email"
                value={email}
                onChange={setEmail}
                onKeyDown={(e: any) => {
                    if (e.key == 'Enter') {
                        save()
                    }
                }}
            />
            <Button loading={saving} intent="primary" text="Change email" onClick={save} />
        </Dialog>
    )
}

const ReactiveSubscription = ({ user, close, onSave }: UserActionProps) => {
    const [days, setDays] = useState(30)
    const [saving, setSaving] = useState(false)

    const save = () => {
        setSaving(true)
        axiosPost('admin/users/reactivate-subscription', {
            days,
            user_id: user?.id,
        })
            .then(() => {
                Toasts.success(`Trial subscription created with ${days} trial days!`)
                setSaving(false)
                close()
                onSave()
            })
            .catch(() => {
                setSaving(false)
            })
    }

    return (
        <Dialog
            isOpen={!!user}
            onClose={close}
            className="p-3"
            canEscapeKeyClose
            canOutsideClickClose
        >
            <p>
                You are reactivating the subscriptions for {user?.email}'s account (user id{' '}
                {user?.id}). This will create a new trial subscription&nbsp; with {days} days to the
                end of their trial period.
            </p>
            <FormInput
                disabled={saving}
                type="number"
                label="Days"
                value={days.toString()}
                onChange={(v) => setDays(parseInt(v))}
            />
            <Button
                loading={saving}
                intent="primary"
                text="Reactivate subscription"
                onClick={save}
            />
        </Dialog>
    )
}

const filters: FilterI[] = [
    {
        key: 'subscriptions.status',
        name: 'Subscription Status',
        type: 'select',
        options: [
            {
                value: SubscriptionStatus.Incomplete,
                name: 'Incomplete',
            },
            {
                value: SubscriptionStatus.IncompleteExpired,
                name: 'Incomplete Expired',
            },
            {
                value: SubscriptionStatus.Trialing,
                name: 'Trialing',
            },
            {
                value: SubscriptionStatus.Active,
                name: 'Active',
            },
            {
                value: SubscriptionStatus.PastDue,
                name: 'PastDue',
            },
            {
                value: SubscriptionStatus.Canceled,
                name: 'Canceled',
            },
            {
                value: SubscriptionStatus.Unpaid,
                name: 'Unpaid',
            },
        ],
    },
]
